import React, { useEffect, useState, useRef } from 'react';
import classnames from 'classnames';

import styles from './input.module.scss';

import type { ReactNode, CSSProperties, ChangeEvent, KeyboardEvent } from 'react';

export type InputRef = HTMLInputElement;

export interface InputProps extends Omit<React.InputHTMLAttributes<InputRef>, 'prefix'> {

  /**
   * @description  语义化结构 class		
   */
  className?: string;

  /**
   * @description  语义化结构 class		
   */
  rootClassName?: string;

  /**
   * @description  输入框默认内容	
   */
  defaultValue?: string;

  /**
   * @description 是否禁用状态，默认为 false	
   */
  disabled?: boolean;

  /**
    * @description 输入框只读状态
    */
  readonly?: boolean;

  /**
   * @description  输入框的 id			
   */
  id?: string;

  /**
   * @description 最大长度	
   */
  maxLength?: number;

  /**
   * @description  输入框提示值
   */
  placeholder?: string;

  /**
  * @description  输入框name
  */
  name?: string;

  /**
   * @description  是否展示字数		
   */
  showCount?: boolean;

  /**
   * @description  设置校验状态	
   */
  status?: 'error' | 'warning';

  /**
   * @description  语义化结构 style			
   */
  style?: CSSProperties;

  /**
   * @description  语义化结构 style			
   */
  rootStyle?: CSSProperties;

  /**
   * @description  声明 input 类型，同原生 input 标签的 type 属性，见：MDN(请直接使用 Input.TextArea 代替 type="textarea")
   */
  type?: string;

  /**
   * @description  输入框内容	
   */
  value?: string;

  /**
   * @description 输入框内容变化时的回调		
   */
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;

  /**
   * @description 按下回车的回调		
   */
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;

  /**
   * @description 带有前缀的 input	
   */
  prefix?: ReactNode;

  /**
   * @description 带有后缀的 input	
   */
  suffix?: ReactNode;

  positive?: boolean;

}

const Input = React.forwardRef<InputRef, InputProps>((props, ref) => {

  const prefixCls = 'web_components_input';

  const {
    className,
    defaultValue,
    disabled = false,
    readOnly = false,
    id,
    maxLength,
    placeholder,
    showCount = false,
    status,
    style,
    name,
    type = 'text',
    value,
    onChange,
    onKeyDown,
    prefix,
    suffix,
    rootClassName,
    rootStyle,
    positive = false,
    ...restInputProps
  } = props;

  const classes = classnames(prefixCls, className);

  const [inputValue, setInputValue] = useState<string | undefined>(defaultValue ?? value);
  const [realValue, setRealValue] = useState<string | undefined>(defaultValue ?? value);
  const [isComposing, setIsComposing] = useState(false);

  useEffect(() => {
    if (!isComposing) {
      setRealValue(inputValue);
    }
  }, [inputValue, isComposing])

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const _event = event;
      // 过滤掉负数输入（如果positive属性为true）
      if (positive && type === 'number') {
        _event.target.value = Number(_event.target.value.toString().replace(/\-/g, '')) as any;
      }
      setInputValue(_event.target.value);
      onChange?.(_event); // 调用onChange回调
    };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      onKeyDown?.(event);
    }
  };

  const handleCompositionStart = () => {
    setIsComposing(true);
  };

  const handleCompositionEnd = () => {
    setIsComposing(false);
  };


  return (
    <div 
      className={
        classnames(
          styles[prefixCls],
          rootClassName,
          {
            [styles[`${prefixCls}_disabled`]]: disabled,
            [styles[`${prefixCls}_${status}`]]: status
          }
        )
      }
      style={rootStyle}>
      {
        prefix && (
          <div className={`${prefixCls}_prefix`}>{prefix}</div>
        )
      }
      <input
        ref={ref}
        className={classes}
        defaultValue={defaultValue}
        disabled={disabled}
        id={id}
        maxLength={maxLength}
        name={name}
        readOnly={readOnly}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        onCompositionStart={handleCompositionStart}
        onCompositionEnd={handleCompositionEnd}
        style={style}
        type={type}
        value={value}
        {...restInputProps}
      />
      {
        suffix && (
          <div className={`${prefixCls}_suffix`}>{suffix}</div>
        )
      }
      {showCount && maxLength && (
        <div className={`${prefixCls}_count`}>
          <span className={`${prefixCls}_count_text`}>
            {realValue?.length || 0}/{maxLength}
          </span>
        </div>
      )}
    </div>
  );
});

export default Input;